import type {
	ExploreFilterChipType,
	LessonSection,
	LessonSortType,
	SortOrder,
	TopicFilter,
} from "@memorang/ui/components/LessonsExploreScreen/types";
import { useCallback, useMemo, useState } from "react";
import { exploreModules, exploreTopicFilters } from "../constants";
import {
	type FilterCriteria,
	compareLessons,
	isLessonSortType,
	lessonMatchesFilters,
} from "../helpers";

export const useLessonsExplore = () => {
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
	const [isRecentSelected, setIsRecentSelected] = useState(false);
	const [isCompletedSelected, setIsCompletedSelected] = useState(false);
	const [isPremiumSelected, setIsPremiumSelected] = useState(false);
	const [sortType, setSortType] = useState<LessonSortType>("popular");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

	const filteredLessons = useMemo(() => {
		const criteria: FilterCriteria = {
			searchQuery,
			selectedTopics,
			isRecentSelected,
			isCompletedSelected,
			isPremiumSelected,
		};
		return exploreModules
			.filter((lesson) => lessonMatchesFilters(lesson, criteria))
			.sort((a, b) => compareLessons(a, b, sortType, sortOrder));
	}, [
		searchQuery,
		selectedTopics,
		isRecentSelected,
		isCompletedSelected,
		isPremiumSelected,
		sortType,
		sortOrder,
	]);

	const sections = useMemo<LessonSection[]>(
		() =>
			filteredLessons.length
				? [{ title: "All modules", data: filteredLessons }]
				: [],
		[filteredLessons],
	);

	const handleToggleFilter = useCallback(
		(type: Exclude<ExploreFilterChipType, "topics">) => {
			switch (type) {
				case "all": {
					setSearchQuery("");
					setSelectedTopics([]);
					setIsRecentSelected(false);
					setIsCompletedSelected(false);
					setIsPremiumSelected(false);
					return;
				}
				case "recent": {
					setIsRecentSelected((prev) => !prev);
					return;
				}
				case "completed": {
					setIsCompletedSelected((prev) => !prev);
					return;
				}
				case "premium": {
					setIsPremiumSelected((prev) => !prev);
					return;
				}
				default:
					return;
			}
		},
		[],
	);

	const handleSelectSort = useCallback((type: string) => {
		if (!isLessonSortType(type)) {
			return;
		}
		setSortType((prev) => {
			if (prev === type) {
				setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
				return prev;
			}
			setSortOrder(type === "popular" ? "desc" : "asc");
			return type;
		});
	}, []);

	const handleApplyTopicFilters = useCallback((filters: TopicFilter[]) => {
		setSelectedTopics(
			filters
				.filter((filter) => filter.isSelected)
				.map((filter) => filter.title),
		);
	}, []);

	const handleClearFilters = useCallback(() => {
		setSearchQuery("");
		setSelectedTopics([]);
		setIsRecentSelected(false);
		setIsCompletedSelected(false);
		setIsPremiumSelected(false);
	}, []);

	const topicFilters = useMemo(
		() =>
			exploreTopicFilters.map((filter) => ({
				...filter,
				isSelected: selectedTopics.includes(filter.title),
			})),
		[selectedTopics],
	);

	return {
		searchQuery,
		onChangeSearch: setSearchQuery,
		filterState: {
			isRecentSelected,
			isCompletedSelected,
			isPremiumSelected,
			selectedTopics,
		},
		onToggleFilter: handleToggleFilter,
		sections,
		totalLessons: exploreModules.length,
		sortType,
		sortOrder,
		onSelectSort: handleSelectSort,
		topicFilters,
		onApplyTopicFilters: handleApplyTopicFilters,
		onClearFilters: handleClearFilters,
	};
};
